import { useState, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin } from "lucide-react";

export const QueryForm = forwardRef(({ presetBank }, ref) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    loan_amount: 500000, tenure_months: 36,
    bank_interested: presetBank || "", message: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill your name, phone and email");
      return;
    }
    if (!/^\+?\d{10,15}$/.test(form.phone.replace(/\s|-/g, ""))) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to be contacted before submitting");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/queries", { ...form, loan_amount: Number(form.loan_amount), tenure_months: Number(form.tenure_months) });
      toast.success("Query submitted! Our team will call you within 2 business hours.");
      setDone(true);
      setForm({ name: "", phone: "", email: "", loan_amount: 500000, tenure_months: 36, bank_interested: "", message: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Talk to us</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-slate-900">
            One form. Zero spam. Real people call you back.
          </h2>
          <p className="mt-3 text-slate-600">
            Share what you need. Our loan advisors will find the best offer for your profile and walk you through the process — free, no obligation.
          </p>

          <div className="mt-8 space-y-4">
            <ContactRow icon={<Phone className="w-4 h-4" />} label="Phone" value="9312201464" href="tel:9312201464" testId="contact-phone" />
            <ContactRow icon={<Mail className="w-4 h-4" />} label="Email" value="loan81.info@gmail.com" href="mailto:loan81.info@gmail.com" testId="contact-email" />
            <ContactRow icon={<MapPin className="w-4 h-4" />} label="Hours" value="Mon–Sat, 9 AM – 8 PM IST" />
          </div>

          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Why 50,000+ chose Loan81
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              <li>✓ Compare rates across 15+ verified lenders</li>
              <li>✓ Zero platform fees — we're paid by lenders</li>
              <li>✓ 100% confidential — no data selling</li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="h-full min-h-[400px] bg-emerald-50 border border-emerald-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center" data-testid="query-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-4" />
              <div className="font-display text-2xl font-semibold text-slate-900">We got it.</div>
              <p className="text-slate-600 mt-2 max-w-md">A loan advisor will call you at your registered number within 2 business hours with the best matching offers.</p>
              <Button data-testid="query-new" onClick={() => setDone(false)} variant="outline" className="mt-6">Submit another query</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 lg:p-8 space-y-5" data-testid="query-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name*" testId="query-name-wrap">
                  <Input data-testid="query-name" required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Priya Sharma" className="bg-white h-11" />
                </Field>
                <Field label="Phone Number*" testId="query-phone-wrap">
                  <Input data-testid="query-phone" required type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="9312201464" className="bg-white h-11" />
                </Field>
              </div>

              <Field label="Email*">
                <Input data-testid="query-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="bg-white h-11" />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Loan Amount (₹)*">
                  <Input data-testid="query-amount" required type="number" min={10000} value={form.loan_amount} onChange={(e) => update("loan_amount", e.target.value)} className="bg-white h-11" />
                </Field>
                <Field label="Tenure (months)">
                  <Input data-testid="query-tenure" type="number" min={6} max={96} value={form.tenure_months} onChange={(e) => update("tenure_months", e.target.value)} className="bg-white h-11" />
                </Field>
              </div>

              <Field label="Preferred Bank / NBFC (optional)">
                <Select value={form.bank_interested} onValueChange={(v) => update("bank_interested", v)}>
                  <SelectTrigger data-testid="query-bank" className="bg-white h-11">
                    <SelectValue placeholder="Any / No preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any / No preference</SelectItem>
                    <SelectItem value="HDFC Bank">HDFC Bank</SelectItem>
                    <SelectItem value="ICICI Bank">ICICI Bank</SelectItem>
                    <SelectItem value="SBI">State Bank of India</SelectItem>
                    <SelectItem value="Axis Bank">Axis Bank</SelectItem>
                    <SelectItem value="Kotak Mahindra">Kotak Mahindra</SelectItem>
                    <SelectItem value="Bajaj Finserv">Bajaj Finserv</SelectItem>
                    <SelectItem value="Tata Capital">Tata Capital</SelectItem>
                    <SelectItem value="IDFC First">IDFC First</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Anything else? (optional)">
                <Textarea data-testid="query-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Employment type, credit concerns, urgency, etc." className="bg-white min-h-[90px]" />
              </Field>

              <label className="flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed cursor-pointer" htmlFor="query-consent">
                <input
                  id="query-consent"
                  data-testid="query-consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => update("consent", e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                />
                <span>
                  I agree to be contacted by Loan81 and relevant partner banks/NBFCs via call, SMS, WhatsApp
                  or email regarding this enquiry, even if my number is registered on DND/NCPR. Read our{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-700 underline">Privacy Policy</a>
                  {" "}and{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-700 underline">Terms of Use</a>.
                </span>
              </label>

              <Button
                data-testid="query-submit"
                type="submit"
                disabled={submitting || !form.consent}
                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-semibold active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit & Get Called Back"}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">Loan81 is a loan comparison platform, not a lender. We never sell your data.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
});
QueryForm.displayName = "QueryForm";

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const ContactRow = ({ icon, label, value, href, testId }) => {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} data-testid={testId} className="flex items-center gap-3 group">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 border border-emerald-100">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">{label}</div>
        <div className={`text-base font-semibold text-slate-900 ${href ? "group-hover:text-emerald-600 transition-colors" : ""}`}>{value}</div>
      </div>
    </Wrap>
  );
};
